import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useCallback, useMemo, useRef, useState } from "react";
import isEqual from "lodash/isEqual";
import styled from "styled-components";
import { useFormikContext, Formik, Form } from "formik";
import i18n from "i18n";
import { Button } from "components/bootstrap";
import Popover from "components/common/Popover";
import { Icon, ModalSubmit, FormikInput, Link } from "components/common";
import { ConfigurationsActions } from "stores/configurations/ConfigurationsStore";
import { ConfigurationType } from "components/configurations/ConfigurationTypes";
import useSearchConfiguration from "hooks/useSearchConfiguration";
import useUserDateTime from "hooks/useUserDateTime";
import Routes from "routing/Routes";
import generateId from "logic/generateId";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import {
  normalizeFromPickerForSearchBar,
  normalizeFromSearchBarForBackend
} from "views/logic/queries/NormalizeTimeRange";
import { NO_TIMERANGE_OVERRIDE } from "views/Constants";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
const StyledModalSubmit = styled(ModalSubmit)`
  margin-top: 15px;
`;
const isTimerangeEqual = (firstTimerange, secondTimerange) => {
  if (firstTimerange.type !== secondTimerange.type) return false;
  if (firstTimerange.type === "keyword")
    return firstTimerange.keyword === secondTimerange.keyword;
  return isEqual(firstTimerange, secondTimerange);
};
const ConfigurationsLink = ({ children = void 0 }) => /* @__PURE__ */ React.createElement(Link, { to: Routes.SYSTEM.CONFIGURATIONS, target: "_blank" }, children);
const validate = ({ description }) => {
  if (!description) {
    return { description: i18n.t("time_range.preset_description_required", { ns: "search" }) };
  }
  return {};
};
const TimeRangeAddToQuickListForm = ({ children = void 0, addTimerange, toggleModal, show }) => {
  const { t } = useTranslation("search");
  const { userTimezone } = useUserDateTime();
  const { config } = useSearchConfiguration();
  const {
    values: { timeRangeTabs, activeTab }
  } = useFormikContext();
  const activeTabTimeRange = timeRangeTabs[activeTab];
  const equalTimerange = useMemo(
    () => {
      var _a;
      return (_a = config == null ? void 0 : config.quick_access_timerange_presets) == null ? void 0 : _a.find(
        (existingPreset) => isTimerangeEqual(
          existingPreset.timerange,
          normalizeFromSearchBarForBackend(
            normalizeFromPickerForSearchBar(activeTabTimeRange),
            userTimezone
          )
        )
      );
    },
    [config, activeTabTimeRange, userTimezone]
  );
  const onSubmit = ({ description }) => addTimerange(description);
  return /* @__PURE__ */ React.createElement(Popover, { position: "left", withArrow: true, opened: show, withinPortal: false }, /* @__PURE__ */ React.createElement(Popover.Target, null, children), /* @__PURE__ */ React.createElement(
    Popover.Dropdown,
    {
      title: t("time_range.save_as_preset"),
      id: "time-range-preset-popover",
      "data-testid": "time-range-preset-popover"
    },
    /* @__PURE__ */ React.createElement(Formik, { onSubmit, initialValues: { description: "" }, validate }, ({ isValid, submitForm }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "text",
        name: "description",
        id: "time-range-preset-description",
        placeholder: t("time_range.preset_description_placeholder"),
        "aria-label": t("time_range.preset_description_label"),
        formGroupClassName: ""
      }
    ), !!equalTimerange && /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Icon, { name: "warning" }), " ", /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "time_range.preset_similar",
        components: { configurationsLink: /* @__PURE__ */ React.createElement(ConfigurationsLink, null) }
      }
    ), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("i", null, "(", equalTimerange.description, ")")), /* @__PURE__ */ React.createElement(
      StyledModalSubmit,
      {
        disabledSubmit: !isValid,
        submitButtonType: "button",
        onSubmit: submitForm,
        submitButtonText: t("time_range.save_preset"),
        isAsyncSubmit: false,
        displayCancel: true,
        onCancel: toggleModal,
        bsSize: "small"
      }
    )))
  ));
};
const SaveTimeRangeAsPresetButton = () => {
  var _a;
  const { t } = useTranslation("search");
  const { userTimezone } = useUserDateTime();
  const {
    values: { timeRangeTabs, activeTab },
    errors
  } = useFormikContext();
  const formTarget = useRef();
  const activeTabTimeRange = timeRangeTabs[activeTab];
  const { config, refresh } = useSearchConfiguration();
  const [showForm, setShowForm] = useState(false);
  const sendTelemetry = useSendTelemetry("search-bar");
  const isValidTimeRange = !((_a = errors.timeRangeTabs) == null ? void 0 : _a[activeTab]);
  const toggleModal = useCallback(() => {
    setShowForm((cur) => !cur);
  }, []);
  const addTimerange = useCallback(
    (description) => {
      const timeRangePreset = {
        description,
        timerange: activeTabTimeRange ? normalizeFromSearchBarForBackend(
          normalizeFromPickerForSearchBar(activeTabTimeRange),
          userTimezone
        ) : NO_TIMERANGE_OVERRIDE,
        id: generateId()
      };
      ConfigurationsActions.update(ConfigurationType.SEARCHES_CLUSTER_CONFIG, {
        ...config,
        quick_access_timerange_presets: [...config.quick_access_timerange_presets, timeRangePreset]
      }).then(() => {
        refresh();
        toggleModal();
      });
      if (timeRangePreset) {
        sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_TIMERANGE_PRESET_ADD_QUICK_ACCESS, {
          app_pathname: "search",
          event_details: {
            timerange: timeRangePreset.timerange,
            id: timeRangePreset.id
          }
        });
      }
    },
    [config, refresh, sendTelemetry, activeTabTimeRange, toggleModal, userTimezone]
  );
  return /* @__PURE__ */ React.createElement(TimeRangeAddToQuickListForm, { show: showForm, addTimerange, toggleModal }, /* @__PURE__ */ React.createElement(
    Button,
    {
      disabled: !isValidTimeRange,
      title: t("time_range.save_current_as_preset"),
      ref: formTarget,
      bsSize: "small",
      onClick: toggleModal
    },
    t("time_range.save_as_preset")
  ));
};
export default SaveTimeRangeAsPresetButton;
